import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import api from '../../services/api';
import './ChatStickers.css';

const CLOUD_NAME = 'dtxar5b36';
const UPLOAD_PRESET = 'bordergarita_uploads';
const UPLOAD_FOLDER = 'chat_stickers';

async function uploadPngToCloudinary(file) {
  const form = new FormData();
  form.append('file', file);
  form.append('upload_preset', UPLOAD_PRESET);
  form.append('folder', UPLOAD_FOLDER);
  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
    { method: 'POST', body: form }
  );
  const json = await res.json();
  if (!res.ok || !json.secure_url) {
    throw new Error(json.error?.message || 'Upload failed');
  }
  return {
    url: json.secure_url,
    publicId: json.public_id,
  };
}

export default function ChatStickers() {
  const toast = useRef(null);
  const fileRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [active, setActive] = useState(true);
  const [url, setUrl] = useState('');
  const [publicId, setPublicId] = useState('');
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/chat_stickers');
      setItems(data.items || []);
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.message || 'Failed to load stickers',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setSortOrder(items.length);
    setActive(true);
    setUrl('');
    setPublicId('');
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setName(item.name || '');
    setSortOrder(item.sortOrder || 0);
    setActive(item.active !== false);
    setUrl(item.url || '');
    setPublicId(item.publicId || '');
    setDialogOpen(true);
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/\.png$/i.test(file.name) && file.type !== 'image/png') {
      toast.current?.show({
        severity: 'warn',
        summary: 'PNG only',
        detail: 'Upload transparent PNG stickers for best results.',
      });
    }
    setUploading(true);
    try {
      const uploaded = await uploadPngToCloudinary(file);
      setUrl(uploaded.url);
      setPublicId(uploaded.publicId);
      if (!name) {
        setName(file.name.replace(/\.png$/i, '').slice(0, 80));
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Upload failed',
        detail: err.message || 'Could not upload',
      });
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!url) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Missing image',
        detail: 'Upload a PNG first',
      });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim() || 'Sticker',
        url,
        publicId: publicId || undefined,
        active,
        sortOrder: Number(sortOrder) || 0,
      };
      if (editing?.id) {
        await api.put(`/chat_stickers/${editing.id}`, payload);
      } else {
        await api.post('/chat_stickers', payload);
      }
      setDialogOpen(false);
      await load();
      toast.current?.show({
        severity: 'success',
        summary: 'Saved',
        detail: 'Sticker saved',
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.message || 'Failed to save',
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = (item) => {
    confirmDialog({
      message: `Delete sticker “${item.name}”?`,
      header: 'Delete sticker',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await api.delete(`/chat_stickers/${item.id}`);
          await load();
          toast.current?.show({
            severity: 'success',
            summary: 'Deleted',
            detail: 'Sticker removed',
          });
        } catch (err) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: err?.response?.data?.message || 'Failed to delete',
          });
        }
      },
    });
  };

  return (
    <div className="chat-stickers-page">
      <Toast ref={toast} />
      <ConfirmDialog />
      <Card>
        <div className="chat-stickers-header">
          <div>
            <h2 className="m-0">Chat stickers</h2>
            <p className="text-600 m-0 mt-1">
              PNG stickers for the mobile chat picker (WhatsApp-style Stickers tab).
            </p>
          </div>
          <Button
            label="Add sticker"
            icon="pi pi-plus"
            className="p-button-primary primary-button"
            onClick={openCreate}
          />
        </div>

        {loading ? (
          <div className="chat-stickers-empty">Loading…</div>
        ) : items.length === 0 ? (
          <div className="chat-stickers-empty">
            No stickers yet. Upload a transparent PNG to get started.
          </div>
        ) : (
          <div className="chat-stickers-grid">
            {items.map((item) => (
              <div
                key={item.id}
                className={`chat-sticker-card ${item.active ? '' : 'is-inactive'}`}
              >
                <div className="chat-sticker-preview">
                  <img src={item.url} alt={item.name} />
                </div>
                <div className="chat-sticker-meta">
                  <strong>{item.name}</strong>
                  <span>
                    #{item.sortOrder} · {item.active ? 'Active' : 'Hidden'}
                  </span>
                </div>
                <div className="chat-sticker-actions">
                  <Button
                    icon="pi pi-pencil"
                    className="p-button-text p-button-rounded"
                    onClick={() => openEdit(item)}
                  />
                  <Button
                    icon="pi pi-trash"
                    className="p-button-text p-button-rounded p-button-danger"
                    onClick={() => remove(item)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog
        header={editing ? 'Edit sticker' : 'Add sticker'}
        visible={dialogOpen}
        style={{ width: 'min(440px, 94vw)' }}
        onHide={() => setDialogOpen(false)}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setDialogOpen(false)}
            />
            <Button
              label="Save"
              icon="pi pi-check"
              className="p-button-primary primary-button"
              loading={saving}
              onClick={save}
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3">
          <div className="chat-sticker-upload-box">
            {url ? (
              <img src={url} alt="" className="chat-sticker-upload-preview" />
            ) : (
              <span className="text-600">PNG preview</span>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/webp,image/jpeg"
              hidden
              onChange={onPickFile}
            />
            <Button
              label={uploading ? 'Uploading…' : url ? 'Replace PNG' : 'Upload PNG'}
              icon="pi pi-upload"
              className="p-button-outlined"
              loading={uploading}
              onClick={() => fileRef.current?.click()}
            />
          </div>
          <label className="flex flex-column gap-1">
            <span className="font-semibold">Name</span>
            <InputText value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="flex flex-column gap-1">
            <span className="font-semibold">Sort order</span>
            <InputNumber
              value={sortOrder}
              onValueChange={(e) => setSortOrder(e.value ?? 0)}
              useGrouping={false}
            />
          </label>
          <div className="flex align-items-center gap-2">
            <InputSwitch checked={active} onChange={(e) => setActive(e.value)} />
            <span>Active in app</span>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
